import { useEffect, useState, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getCategories } from "@/api/productApi"
import { createAdminProduct } from "@/api/adminProductApi"
import { deleteS3Image, getS3PresignedUrl } from "@/api/s3Api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, X, Trash2 } from "lucide-react"
import ReactQuill from "react-quill-new"
import "react-quill-new/dist/quill.snow.css"

type Category = {
    categoryId: number
    name: string
}

// 💡 [추가] 옵션 관련 타입 정의
type OptionGroup = {
    name: string;      // 옵션명 (예: 색상)
    valueStr: string;  // 옵션값 문자열 (예: 레드,블루,블랙)
}

type OptionCombination = {
    name: string;            // 조합된 옵션명 (예: 레드 / S)
    additionalPrice: number; // 추가 금액
    stockQuantity: number;   // 재고 수량
    isUse: boolean;          // 사용 여부
}

export default function AdminProductCreatePage() {
    const navigate = useNavigate()

    const [categories, setCategories] = useState<Category[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("상품 등록")

    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
    const [thumbnailPreview, setThumbnailPreview] = useState<string>("")

    const [detailFiles, setDetailFiles] = useState<File[]>([])
    const [detailPreviews, setDetailPreviews] = useState<string[]>([])

    const [editorUploadedImages, setEditorUploadedImages] = useState<string[]>([])

    const quillRef = useRef<any>(null)

    // ==========================================
    // 💡 [추가] 옵션 상태 관리
    // ==========================================
    const [hasOptions, setHasOptions] = useState(false) // 옵션 사용 여부
    const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([{ name: "", valueStr: "" }])
    const [optionCombinations, setOptionCombinations] = useState<OptionCombination[]>([])

    // 일괄 적용 상태
    const [batchPrice, setBatchPrice] = useState<number>(0)
    const [batchStock, setBatchStock] = useState<number>(999)

    const handleAddOptionGroup = () => {
        if (optionGroups.length >= 3) {
            return alert("옵션은 최대 3개(3 Depth)까지만 추가할 수 있습니다.")
        }
        setOptionGroups([...optionGroups, { name: "", valueStr: "" }])
    }

    const handleRemoveOptionGroup = (indexToRemove: number) => {
        setOptionGroups(optionGroups.filter((_, idx) => idx !== indexToRemove))
    }

    const handleOptionGroupChange = (index: number, field: keyof OptionGroup, value: string) => {
        const newGroups = [...optionGroups]
        newGroups[index][field] = value
        setOptionGroups(newGroups)
    }

    // 💡 모든 경우의 수(Cartesian Product)를 생성하는 로직
    const generateCombinations = () => {
        const validGroups = optionGroups.filter(g => g.name.trim() !== "" && g.valueStr.trim() !== "")
        if (validGroups.length === 0) {
            return alert("옵션명과 옵션값을 정확히 입력해주세요.")
        }

        const parsedGroups = validGroups.map(g => ({
            name: g.name,
            values: g.valueStr.split(",").map(v => v.trim()).filter(v => v !== "")
        }))

        if (parsedGroups.some(g => g.values.length === 0)) {
            return alert("콤마(,)를 기준으로 옵션값을 하나 이상 입력해주세요.")
        }

        // 재귀함수를 이용한 모든 경우의 수 조합
        const combine = (groups: {name: string, values: string[]}[], idx: number): string[] => {
            if (idx === groups.length) return [""]
            const currentValues = groups[idx].values
            const nextCombinations = combine(groups, idx + 1)
            const result: string[] = []
            for (const val of currentValues) {
                for (const next of nextCombinations) {
                    result.push(next ? `${val} / ${next}` : val)
                }
            }
            return result
        }

        const combNames = combine(parsedGroups, 0)
        const newCombs = combNames.map(name => ({
            name,
            additionalPrice: 0,
            stockQuantity: 999, // 기본 재고
            isUse: true
        }))

        setOptionCombinations(newCombs)
    }

    const handleCombinationChange = (index: number, field: keyof OptionCombination, value: any) => {
        const newCombs = [...optionCombinations]
        newCombs[index] = { ...newCombs[index], [field]: value }
        setOptionCombinations(newCombs)
    }

    const handleBatchApply = () => {
        if (optionCombinations.length === 0) return alert("옵션 조합을 먼저 생성해주세요.")
        const newCombs = optionCombinations.map(comb => ({
            ...comb,
            additionalPrice: batchPrice,
            stockQuantity: batchStock
        }))
        setOptionCombinations(newCombs)
    }
    // ==========================================

    const handleDescriptionChange = (content: string) => {
        setForm((prev) => ({ ...prev, description: content }))
    }

    const imageHandler = () => {
        const input = document.createElement("input")
        input.setAttribute("type", "file")
        input.setAttribute("accept", "image/*")
        input.click()

        input.onchange = async () => {
            const file = input.files ? input.files[0] : null
            if (!file) return

            try {
                const { presignedUrl, imageUrl } = await getS3PresignedUrl(file.name)
                await fetch(presignedUrl, {
                    method: "PUT",
                    body: file,
                    headers: { "Content-Type": file.type },
                })

                setEditorUploadedImages((prev) => [...prev, imageUrl])

                const editor = quillRef.current?.getEditor()
                if (!editor) return

                const range = editor.getSelection()
                const position = range ? range.index : editor.getLength()

                editor.insertEmbed(position, "image", imageUrl)
                editor.setSelection(position + 1, 0)
            } catch (error) {
                console.error("에디터 이미지 업로드 에러:", error)
                alert("이미지 업로드에 실패했습니다.")
            }
        }
    }

    const handleCancel = async () => {
        if (editorUploadedImages.length > 0) {
            if (!confirm("작성을 취소하시겠습니까? (업로드된 에디터 이미지가 삭제됩니다)")) return
            for (const url of editorUploadedImages) {
                await deleteS3Image(url)
            }
        }
        navigate("/admin/products")
    }

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ color: [] }, { background: [] }],
                ["link", "image", "clean"],
            ],
            handlers: {
                image: imageHandler,
            },
        },
    }), [])

    const [form, setForm] = useState({
        categoryId: "", productName: "", productCode: "", brandName: "",
        summary: "", description: "", price: "", discountPrice: "",
        stockQuantity: "", status: "SELLING", visibleYn: "Y",
    })

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getCategories()
                if (response.success || response.data) {
                    setCategories(response.data.items || response.data)
                }
            } catch (error) {
                console.error(error)
            }
        }
        fetchCategories()
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setThumbnailFile(file)
            setThumbnailPreview(URL.createObjectURL(file))
        }
    }

    const removeThumbnail = () => {
        setThumbnailFile(null)
        setThumbnailPreview("")
    }

    const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        if (files.length > 0) {
            setDetailFiles((prev) => [...prev, ...files])
            const newPreviews = files.map((file) => URL.createObjectURL(file))
            setDetailPreviews((prev) => [...prev, ...newPreviews])
        }
    }

    const removeDetail = (indexToRemove: number) => {
        setDetailFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
        setDetailPreviews((prev) => prev.filter((_, index) => index !== indexToRemove))
    }

    const uploadToS3 = async (file: File): Promise<string> => {
        const { presignedUrl, imageUrl } = await getS3PresignedUrl(file.name)
        await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
        })
        return imageUrl
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!form.categoryId) return alert("카테고리를 선택해주세요.")
        if (!thumbnailFile) return alert("대표 이미지를 반드시 등록해주세요.")

        // 💡 옵션 사용 시, 옵션 조합이 비어있는지 검증
        if (hasOptions && optionCombinations.length === 0) {
            return alert("옵션 사용 시, 최소 1개 이상의 옵션 조합을 생성해야 합니다.")
        }

        let uploadedThumbnailUrl = ""
        const uploadedDetailUrls: string[] = []
        try {
            setLoading(true)

            setLoadingText("이미지 업로드 중...")
            uploadedThumbnailUrl = await uploadToS3(thumbnailFile)

            for (const file of detailFiles) {
                const url = await uploadToS3(file)
                uploadedDetailUrls.push(url)
            }

            setLoadingText("상품 정보 저장 중...")

            // 💡 [수정됨] payload에 옵션 데이터 추가
            const payload = {
                categoryId: Number(form.categoryId),
                productName: form.productName,
                productCode: form.productCode || undefined,
                brandName: form.brandName || undefined,
                summary: form.summary || undefined,
                description: form.description || undefined,
                price: Number(form.price),
                discountPrice: form.discountPrice ? Number(form.discountPrice) : undefined,
                stockQuantity: hasOptions ? undefined : Number(form.stockQuantity), // 옵션 사용 시 기본 재고 무시 가능
                status: form.status,
                visibleYn: form.visibleYn,
                thumbnailUrl: uploadedThumbnailUrl,
                imageUrls: uploadedDetailUrls,

                // 💡 생성된 옵션 정보를 백엔드로 전송
                hasOptions: hasOptions,
                options: hasOptions ? optionCombinations : []
            }

            // @ts-ignore
            const response = await createAdminProduct(payload)

            if (response) {
                for (const url of editorUploadedImages) {
                    if (!form.description.includes(url)) {
                        await deleteS3Image(url)
                    }
                }
                alert("상품이 성공적으로 등록되었습니다.")
                navigate("/admin/products")
            }
        } catch (error) {
            console.error(error)
            setLoadingText("업로드 취소 처리 중...")
            if (uploadedThumbnailUrl) await deleteS3Image(uploadedThumbnailUrl)
            for (const url of uploadedDetailUrls) await deleteS3Image(url)
            for (const url of editorUploadedImages) await deleteS3Image(url)

            alert("상품 등록 중 오류가 발생하여 업로드가 취소되었습니다.")
        } finally {
            setLoading(false)
            setLoadingText("상품 등록")
        }
    }

    return (
      <AdminLayout>
          <div className="space-y-6 max-w-5xl mx-auto pb-10">
              <div>
                  <h2 className="text-3xl font-bold">상품 등록</h2>
                  <p className="mt-2 text-sm text-slate-500">새로운 상품 정보와 옵션을 입력하고 등록하세요.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 1. 상품 기본 정보 */}
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                      <CardHeader>
                          <CardTitle className="text-xl">상품 기본 정보</CardTitle>
                      </CardHeader>
                      <CardContent className="grid gap-5 md:grid-cols-2">
                          <div className="space-y-2">
                              <Label>카테고리</Label>
                              <select name="categoryId" value={form.categoryId} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="">카테고리 선택</option>
                                  {categories.map((category) => (
                                    <option key={category.categoryId} value={category.categoryId}>{category.name}</option>
                                  ))}
                              </select>
                          </div>
                          <div className="space-y-2">
                              <Label>상품명</Label>
                              <Input name="productName" value={form.productName} onChange={handleChange} placeholder="상품명" className="h-12 rounded-xl" required />
                          </div>
                          <div className="space-y-2">
                              <Label>상품코드</Label>
                              <Input name="productCode" value={form.productCode} onChange={handleChange} placeholder="상품코드" className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2">
                              <Label>브랜드명</Label>
                              <Input name="brandName" value={form.brandName} onChange={handleChange} placeholder="브랜드명" className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                              <Label>상품 요약</Label>
                              <Input name="summary" value={form.summary} onChange={handleChange} placeholder="짧은 상품 설명" className="h-12 rounded-xl" />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                              <Label>상품 상세 설명</Label>
                              <div className="rounded-xl border-slate-200 bg-white">
                                  <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    modules={quillModules}
                                    value={form.description}
                                    onChange={handleDescriptionChange}
                                    placeholder="상품 상세 설명을 자유롭게 작성해주세요."
                                    style={{ height: "300px", marginBottom: "40px" }}
                                  />
                              </div>
                          </div>
                          <div className="space-y-2">
                              <Label>기본 판매가</Label>
                              <Input type="number" name="price" value={form.price} onChange={handleChange} placeholder="판매가" className="h-12 rounded-xl" required />
                          </div>
                          <div className="space-y-2">
                              <Label>할인가</Label>
                              <Input type="number" name="discountPrice" value={form.discountPrice} onChange={handleChange} placeholder="할인가" className="h-12 rounded-xl" />
                          </div>

                          {/* 옵션을 안 쓸 때만 기본 재고수량 입력 */}
                          {!hasOptions && (
                            <div className="space-y-2">
                                <Label>기본 재고수량</Label>
                                <Input type="number" name="stockQuantity" value={form.stockQuantity} onChange={handleChange} placeholder="재고수량" className="h-12 rounded-xl" required />
                            </div>
                          )}

                          <div className="space-y-2">
                              <Label>상태</Label>
                              <select name="status" value={form.status} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="SELLING">판매중</option>
                                  <option value="SOLD_OUT">품절</option>
                                  <option value="HIDDEN">숨김</option>
                              </select>
                          </div>
                          <div className="space-y-2">
                              <Label>노출여부</Label>
                              <select name="visibleYn" value={form.visibleYn} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                  <option value="Y">노출</option>
                                  <option value="N">비노출</option>
                              </select>
                          </div>
                      </CardContent>
                  </Card>

                  {/* ========================================== */}
                  {/* 💡 2. 옵션 설정 영역 추가 */}
                  {/* ========================================== */}
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                      <CardHeader className="bg-slate-50 border-b border-slate-100 flex flex-row items-center justify-between">
                          <div>
                              <CardTitle className="text-xl">상품 옵션 설정</CardTitle>
                              <p className="text-xs text-slate-500 mt-1">색상, 사이즈 등 상품의 옵션을 최대 3 Depth까지 설정할 수 있습니다.</p>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 border border-slate-200 rounded-xl shadow-sm">
                              <input type="checkbox" checked={hasOptions} onChange={(e) => setHasOptions(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded" />
                              <span className="font-bold text-sm text-slate-700">옵션 사용</span>
                          </label>
                      </CardHeader>

                      {hasOptions && (
                        <CardContent className="p-6 space-y-6">
                            <div className="space-y-4">
                                {optionGroups.map((group, idx) => (
                                  <div key={idx} className="flex gap-4 items-start bg-slate-50 p-4 rounded-xl border border-slate-100 relative">
                                      <div className="w-40">
                                          <Label className="text-xs text-slate-500 mb-1 block">옵션명 (예: 색상)</Label>
                                          <Input value={group.name} onChange={(e) => handleOptionGroupChange(idx, 'name', e.target.value)} placeholder="옵션명 입력" className="bg-white" />
                                      </div>
                                      <div className="flex-1">
                                          <Label className="text-xs text-slate-500 mb-1 block">옵션값 (콤마로 구분. 예: 블랙, 화이트)</Label>
                                          <Input value={group.valueStr} onChange={(e) => handleOptionGroupChange(idx, 'valueStr', e.target.value)} placeholder="옵션값을 콤마(,)로 구분하여 입력" className="bg-white" />
                                      </div>
                                      {idx > 0 && (
                                        <Button type="button" variant="ghost" onClick={() => handleRemoveOptionGroup(idx)} className="mt-6 text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-2">
                                            <Trash2 size={18} />
                                        </Button>
                                      )}
                                  </div>
                                ))}

                                <div className="flex gap-3 pt-2">
                                    <Button type="button" variant="outline" onClick={handleAddOptionGroup} disabled={optionGroups.length >= 3} className="rounded-xl border-dashed border-slate-300 text-slate-600">
                                        <Plus size={16} className="mr-1"/> 옵션 추가
                                    </Button>
                                    <Button type="button" onClick={generateCombinations} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white">
                                        옵션 목록(조합) 생성
                                    </Button>
                                </div>
                            </div>

                            {optionCombinations.length > 0 && (
                              <div className="mt-8 border-t border-slate-200 pt-6">
                                  <div className="flex justify-between items-end mb-4">
                                      <h4 className="font-bold text-slate-800">옵션 목록 상세</h4>
                                      {/* 일괄 적용 툴바 */}
                                      <div className="flex gap-2 items-center bg-slate-50 p-2 rounded-xl border border-slate-200">
                                          <span className="text-xs font-bold text-slate-500 px-2">일괄 적용:</span>
                                          <Input type="number" value={batchPrice} onChange={e => setBatchPrice(Number(e.target.value))} placeholder="추가금액" className="w-24 h-8 text-xs bg-white" />
                                          <Input type="number" value={batchStock} onChange={e => setBatchStock(Number(e.target.value))} placeholder="재고수량" className="w-24 h-8 text-xs bg-white" />
                                          <Button type="button" size="sm" onClick={handleBatchApply} className="h-8 bg-slate-800 text-white text-xs rounded-lg">적용</Button>
                                      </div>
                                  </div>

                                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                                      <table className="w-full text-sm text-left">
                                          <thead className="bg-slate-100 text-slate-600 border-b border-slate-200">
                                          <tr>
                                              <th className="p-3 font-bold w-1/3">옵션명</th>
                                              <th className="p-3 font-bold w-1/4">추가 금액(원)</th>
                                              <th className="p-3 font-bold w-1/4">재고 수량(개)</th>
                                              <th className="p-3 font-bold text-center">사용 여부</th>
                                          </tr>
                                          </thead>
                                          <tbody>
                                          {optionCombinations.map((comb, idx) => (
                                            <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                                                <td className="p-3 font-medium text-slate-700">{comb.name}</td>
                                                <td className="p-3">
                                                    <Input type="number" value={comb.additionalPrice} onChange={e => handleCombinationChange(idx, 'additionalPrice', Number(e.target.value))} className="h-9" />
                                                </td>
                                                <td className="p-3">
                                                    <Input type="number" value={comb.stockQuantity} onChange={e => handleCombinationChange(idx, 'stockQuantity', Number(e.target.value))} className="h-9" />
                                                </td>
                                                <td className="p-3 text-center">
                                                    <input type="checkbox" checked={comb.isUse} onChange={e => handleCombinationChange(idx, 'isUse', e.target.checked)} className="w-4 h-4 text-indigo-600 rounded cursor-pointer" />
                                                </td>
                                            </tr>
                                          ))}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                            )}
                        </CardContent>
                      )}
                  </Card>

                  {/* 3. 이미지 업로드 */}
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                      <CardHeader>
                          <CardTitle className="text-xl">상품 이미지</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                          <div className="space-y-3">
                              <Label className="text-base font-semibold">대표 이미지 (1장 필수)</Label>
                              <p className="text-xs text-slate-500">쇼핑몰 메인 및 리스트에 노출되는 이미지입니다.</p>
                              <div>
                                  {thumbnailPreview ? (
                                    <div className="relative w-36 h-36 border border-slate-200 rounded-xl overflow-hidden group">
                                        <img src={thumbnailPreview} alt="대표이미지 미리보기" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button type="button" onClick={removeThumbnail} className="bg-white text-rose-500 rounded-full p-2 hover:bg-rose-50 transition-colors shadow-sm"><X size={20} /></button>
                                        </div>
                                    </div>
                                  ) : (
                                    <label className="flex flex-col items-center justify-center w-36 h-36 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                                        <Plus className="text-slate-400 mb-2" size={28} />
                                        <span className="text-sm text-slate-500 font-medium">이미지 등록</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailChange} />
                                    </label>
                                  )}
                              </div>
                          </div>

                          <hr className="border-slate-200" />

                          <div className="space-y-3">
                              <Label className="text-base font-semibold">상세 이미지 (추가 이미지)</Label>
                              <p className="text-xs text-slate-500">상품 상세 페이지에 순서대로 노출됩니다.</p>
                              <div className="flex flex-wrap gap-4">
                                  {detailPreviews.map((preview, index) => (
                                    <div key={index} className="relative w-36 h-36 border border-slate-200 rounded-xl overflow-hidden group">
                                        <img src={preview} alt={`상세이미지-${index}`} className="w-full h-full object-cover" />
                                        <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-md">{index + 1}</span>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button type="button" onClick={() => removeDetail(index)} className="bg-white text-rose-500 rounded-full p-2 hover:bg-rose-50 transition-colors shadow-sm"><X size={20} /></button>
                                        </div>
                                    </div>
                                  ))}
                                  <label className="flex flex-col items-center justify-center w-36 h-36 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                                      <Plus className="text-slate-400 mb-2" size={28} />
                                      <span className="text-sm text-slate-500 font-medium">{detailPreviews.length > 0 ? "추가 등록" : "이미지 등록"}</span>
                                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleDetailChange} />
                                  </label>
                              </div>
                          </div>

                          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-200">
                              <Button type="button" variant="outline" className="rounded-xl h-12 px-6" onClick={handleCancel} disabled={loading}>취소</Button>
                              <Button type="submit" disabled={loading} className="rounded-xl h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white text-base font-medium transition-all">
                                  {loadingText}
                              </Button>
                          </div>
                      </CardContent>
                  </Card>
              </form>
          </div>
      </AdminLayout>
    )
}